import { ref, watch, onUnmounted, onBeforeUnmount, nextTick } from 'vue';
import { onBeforeRouteLeave } from 'vue-router';

/**
 * composable to handle drawer scroll lock cleanup
 * prevents the body from staying locked when drawer is closed via browser back button
 */
export function useDrawerScrollLock() {
    const isOpen = ref(false);
    
    // handle browser back button
    function handlePopstate(e: PopStateEvent) {
        if (isOpen.value) {
            // prevent default back navigation temporarily
            e.preventDefault();
            isOpen.value = false;
            // force immediate cleanup
            cleanupScrollLock();
            // allow navigation to proceed after cleanup
            setTimeout(() => {
                cleanupScrollLock(); // double-check cleanup
            }, 0);
        } else {
            // always cleanup on any navigation
            cleanupScrollLock();
        }
    }
    
    // cleanup function to reset all possible scroll lock styles
    function cleanupScrollLock() {
        // reset body styles that vaul-vue might set
        document.body.style.overflow = '';
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.left = '';
        document.body.style.right = '';
        document.body.style.paddingRight = '';
        
        // also check html element
        document.documentElement.style.overflow = '';
        document.documentElement.style.position = '';
        
        // remove any data attributes that might be used for scroll locking
        document.body.removeAttribute('data-scroll-locked');
        document.documentElement.removeAttribute('data-scroll-locked');
        
        // check for any vaul-specific attributes
        const vaulAttributes = Array.from(document.body.attributes).filter(attr => 
            attr.name.includes('vaul') || attr.name.includes('radix') || attr.name.includes('data-')
        );
        vaulAttributes.forEach(attr => {
            document.body.removeAttribute(attr.name);
        });
        
        // also clean html element attributes
        const htmlAttributes = Array.from(document.documentElement.attributes).filter(attr => 
            attr.name.includes('vaul') || attr.name.includes('radix') || attr.name.includes('data-')
        );
        htmlAttributes.forEach(attr => {
            document.documentElement.removeAttribute(attr.name);
        });
    }
    
    // watch drawer state to manage event listeners and body overflow
    watch(isOpen, async (open) => {
        if (open) {
            // add listener when drawer opens
            window.addEventListener('popstate', handlePopstate);
        } else {
            // remove listener when drawer closes
            window.removeEventListener('popstate', handlePopstate);
            // wait for next tick to ensure vaul-vue has finished its cleanup attempt
            await nextTick();
            // then force our cleanup
            cleanupScrollLock();
            // double-check after a small delay
            setTimeout(cleanupScrollLock, 100);
        }
    });
    
    // use route guard to cleanup on navigation
    try {
        onBeforeRouteLeave(() => {
            if (isOpen.value) {
                isOpen.value = false;
            }
            cleanupScrollLock();
        });
    } catch (e) {
        // onBeforeRouteLeave might not be available in all contexts
    }
    
    // cleanup on component unmount
    onBeforeUnmount(() => {
        if (isOpen.value) {
            isOpen.value = false;
        }
        cleanupScrollLock();
    });
    
    onUnmounted(() => {
        window.removeEventListener('popstate', handlePopstate);
        // ensure everything is cleaned up when component unmounts
        cleanupScrollLock();
    });
    
    return {
        isOpen
    };
}